/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import * as React from "react";
import { observer } from "mobx-react";
import styled from "styled-components";

import { LoaderCircle, SearchX } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import Button from "@src/components/ui/Button";
import EmptyState from "@src/components/ui/Lists/EmptyState";
import { T9 } from "@src/components/ui/Lists/ListTable";

import { ThemePalette } from "@src/components/Theme";

const Wrapper = styled.div<any>`
  overflow: auto;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
`;
const Scroll = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-width: 980px;
`;
const Separator = styled.div<any>`
  height: 1px;
  background: ${ThemePalette.grayscale[0]};
`;
const Loading = styled.div<any>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
`;
const LoadingText = styled.div<any>`
  font-size: 14px;
  color: ${T9.slate500};
  margin-top: 12px;
`;
const Spinner = styled(LoaderCircle)`
  color: ${T9.brand500};
  animation: mainlist-spin 1s linear infinite;
  @keyframes mainlist-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
const List = styled.div<any>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow: hidden;
`;

const NoResults = styled.div<any>`
  margin-top: 39px;
  text-align: center;
`;
const EmptyList = styled.div<any>`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  justify-content: center;
  flex-grow: 1;
`;
const EmptyListImage = styled.div<any>`
  width: 96px;
  height: 96px;
  background: url("${props => props.source}") center no-repeat;
  background-size: contain;
  margin-bottom: 46px;
`;
const EmptyListMessage = styled.div<any>`
  font-size: 18px;
`;
const EmptyListExtraMessage = styled.div<any>`
  max-width: 700px;
  text-align: center;
  margin: 10px 0 25px 0;
`;
export type ItemComponentProps = {
  key: string;
  item: any;
  selected: boolean;
  /** The list has bulk actions: the row shows its checkbox. */
  selectable?: boolean;
  onClick: () => void;
  onSelectedChange: (checked: boolean) => void;
};
type Props = {
  items: any[];
  selectedItems: any[];
  loading: boolean;
  onSelectedChange: (item: any, checked: boolean) => void;
  onItemClick: (item: any) => void;
  renderItemComponent: (componentProps: ItemComponentProps) => React.ReactNode;
  showEmptyList: boolean;
  emptyListImage?: string | null;
  emptyListMessage?: string;
  emptyListExtraMessage?: string;
  emptyListButtonLabel?: string;
  onEmptyListButtonClick?: () => void;
  mainListWrapperRef?: React.RefObject<HTMLDivElement>;
  emptyListComponent?: React.ReactNode;
  emptyListIcon?: LucideIcon;
  selectable?: boolean;
  /** The sticky table header, drawn inside the scrolling area. */
  headerComponent?: React.ReactNode;
  noResultsLabel?: string;
  onClearFilters?: () => void;
};
@observer
class MainList extends React.Component<Props> {
  renderList() {
    if (!this.props.items || this.props.items.length === 0) {
      return null;
    }

    return (
      <List>
        {this.props.items.map(item => {
          const selected = Boolean(
            this.props.selectedItems.find(i => i.id === item.id),
          );
          return this.props.renderItemComponent({
            key: item.id,
            item,
            selected,
            selectable: this.props.selectable,
            onClick: () => {
              this.props.onItemClick(item);
            },
            onSelectedChange: checked => {
              this.props.onSelectedChange(item, checked);
            },
          });
        })}
      </List>
    );
  }

  renderLoading() {
    return (
      <Loading>
        <Spinner size={32} strokeWidth={1.75} />
        <LoadingText>Loading…</LoadingText>
      </Loading>
    );
  }

  renderNoResults() {
    if (this.props.onClearFilters) {
      return (
        <EmptyState
          compact
          icon={SearchX}
          tone="neutral"
          title={this.props.noResultsLabel || "Nothing matches these filters."}
          message="Try a different search, or clear the filters."
          secondaryLabel="Clear filters"
          onSecondary={this.props.onClearFilters}
        />
      );
    }
    return <NoResults>No results</NoResults>;
  }

  renderEmptyList() {
    if (this.props.emptyListComponent) {
      return this.props.emptyListComponent;
    }

    if (this.props.emptyListIcon) {
      return (
        <EmptyState
          icon={this.props.emptyListIcon}
          title={this.props.emptyListMessage || ""}
          message={this.props.emptyListExtraMessage}
          actionLabel={this.props.emptyListButtonLabel}
          onAction={this.props.onEmptyListButtonClick}
        />
      );
    }

    const renderImage = () => {
      if (this.props.emptyListImage) {
        return <EmptyListImage source={this.props.emptyListImage} />;
      }
      return null;
    };

    const renderButton = () => {
      if (this.props.emptyListButtonLabel) {
        return (
          <Button onClick={this.props.onEmptyListButtonClick}>
            {this.props.emptyListButtonLabel}
          </Button>
        );
      }
      return null;
    };

    return (
      <EmptyList>
        {renderImage()}
        <EmptyListMessage>{this.props.emptyListMessage}</EmptyListMessage>
        <EmptyListExtraMessage>
          {this.props.emptyListExtraMessage}
        </EmptyListExtraMessage>
        {renderButton()}
      </EmptyList>
    );
  }

  render() {
    const renderContent = () => {
      if (this.props.loading) {
        return this.renderLoading();
      }

      if (this.props.showEmptyList) {
        return this.renderEmptyList();
      }

      if (this.props.items.length === 0) {
        return this.renderNoResults();
      }

      return this.renderList();
    };

    if (this.props.headerComponent !== undefined) {
      const showHeader =
        !this.props.loading && !this.props.showEmptyList && this.props.items.length > 0;
      return (
        <Wrapper ref={this.props.mainListWrapperRef}>
          {showHeader ? (
            <Scroll>
              {this.props.headerComponent}
              {renderContent()}
            </Scroll>
          ) : (
            renderContent()
          )}
        </Wrapper>
      );
    }

    return (
      <Wrapper ref={this.props.mainListWrapperRef}>
        {this.props.loading ||
        this.props.items.length === 0 ||
        this.props.showEmptyList ? (
          <Separator />
        ) : null}
        {renderContent()}
      </Wrapper>
    );
  }
}

export default MainList;
